/**
 * Hebrew copy for the Dovach web apps.
 *
 * All strings live in `he.json` keyed by `feature.component` path. Per plan.md
 * §3.13, no inline Hebrew strings are allowed in component code — every visible
 * string must be looked up via `t(key, vars)` so a native Hebrew reviewer can
 * audit one file before any merge.
 */

import strings from "./he.json" with { type: "json" };

export type StringsTree = typeof strings;

export class MissingStringError extends Error {
  override name = "MissingStringError";
}

/**
 * Look up a string by dot-separated key path. Substitutes `{{var}}` placeholders
 * with values from `vars`. Throws if the key path doesn't exist (loud failure
 * is preferable to a "{undefined}" leaking into the UI).
 *
 * @example
 *   t("client_web.upload_progress", { current: 3, total: 5 })
 *   //  → "מעלה 3 מתוך 5 קבצים"
 */
export function t(keyPath: string, vars: Record<string, string | number> = {}): string {
  const parts = keyPath.split(".");
  let cursor: unknown = strings as unknown;
  for (const part of parts) {
    if (cursor === null || typeof cursor !== "object" || !(part in cursor)) {
      throw new MissingStringError(`i18n key not found: ${keyPath}`);
    }
    cursor = (cursor as Record<string, unknown>)[part];
  }
  if (typeof cursor !== "string") {
    throw new MissingStringError(`i18n key '${keyPath}' is not a string`);
  }
  return cursor.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    if (!(name in vars)) {
      throw new MissingStringError(`i18n key '${keyPath}' missing var '${name}'`);
    }
    return String(vars[name]);
  });
}

/** Whole strings tree. Exposed for sanity checks (e.g., axe-core a11y tests). */
export const i18nStrings: StringsTree = strings;
